import { resolvePublicMarkdownRequest, } from "../markdown/index.js";
import { formatPublicCacheControl } from "./renderer.js";
function headerValue(request, name) {
    const entry = Object.entries(request.headers ?? {}).find(([key]) => key.toLowerCase() === name.toLowerCase());
    const value = entry?.[1];
    return typeof value === "string"
        ? value
        : value
            ? Array.from(value).join(", ")
            : "";
}
function defaultAcceptsMarkdown(request) {
    const accept = headerValue(request, "accept");
    return /(?:^|,)\s*text\/markdown(?:\s*;|\s*,|$)/i.test(accept)
        && !/(?:^|,)\s*text\/html(?:\s*;|\s*,|$)/i.test(accept);
}
function withVary(headers, name) {
    const current = headers["Vary"] ?? headers["vary"];
    const values = new Set((current ?? "").split(",").map((value) => value.trim()).filter(Boolean));
    values.add(name);
    delete headers["vary"];
    headers["Vary"] = Array.from(values).join(", ");
}
function methodNotAllowed(method) {
    return {
        statusCode: 405,
        headers: {
            "Content-Type": "text/plain; charset=utf-8",
            "Cache-Control": "no-store",
            Allow: "GET, HEAD",
        },
        body: method === "HEAD" ? "" : "Method not allowed",
    };
}
function cacheHeader(policy) {
    return formatPublicCacheControl(policy);
}
export function createPublicSiteHandler(options) {
    const robotsPath = options.site.normalizePath(options.robotsPath ?? "/robots.txt");
    const documents = new Map(Object.entries(options.documents ?? {}).map(([path, document]) => [
        options.site.normalizePath(path),
        document,
    ]));
    return async (request) => {
        const method = request.method.toUpperCase();
        const head = method === "HEAD";
        if (options.sitemap) {
            const sitemapResponse = await options.sitemap({
                method,
                pathname: request.pathname,
            });
            if (sitemapResponse)
                return {
                    ...sitemapResponse,
                    body: head ? "" : sitemapResponse.body,
                };
        }
        const normalizedPath = options.site.normalizePath(request.pathname);
        const document = documents.get(normalizedPath);
        if (normalizedPath === robotsPath && options.robots !== undefined) {
            if (method !== "GET" && method !== "HEAD")
                return methodNotAllowed(method);
            const body = typeof options.robots === "function"
                ? await options.robots()
                : options.robots;
            return {
                statusCode: 200,
                headers: {
                    "Content-Type": "text/plain; charset=utf-8",
                    "Cache-Control": "public, max-age=3600, stale-if-error=86400",
                },
                body: head ? "" : body,
            };
        }
        if (document) {
            if (method !== "GET" && method !== "HEAD")
                return methodNotAllowed(method);
            const body = await document.render(request);
            return {
                statusCode: 200,
                headers: {
                    "Content-Type": document.contentType,
                    "Cache-Control": document.cacheControl ?? "public, max-age=300, stale-if-error=3600",
                },
                body: head ? "" : body,
            };
        }
        if (method !== "GET" && method !== "HEAD")
            return options.html || options.markdown ? methodNotAllowed(method) : null;
        if (options.markdown) {
            const decision = resolvePublicMarkdownRequest(options.site, {
                requestUrl: request.url,
                acceptsMarkdown: options.acceptsMarkdown?.(request) ?? defaultAcceptsMarkdown(request),
            });
            if (decision.kind === "markdown") {
                const response = await options.markdown({
                    request,
                    routeUrl: decision.routeUrl,
                    formatRedirectPath: decision.formatRedirectPath,
                });
                const headers = {
                    "Content-Type": "text/markdown; charset=utf-8",
                    "Cache-Control": cacheHeader(response.cachePolicy),
                    "X-Markdown-Tokens": String(response.tokenCount),
                    ...(response.retryAfter === undefined ? {} : { "Retry-After": String(response.retryAfter) }),
                };
                if (decision.varyAccept)
                    withVary(headers, "Accept");
                if (response.vary)
                    withVary(headers, response.vary);
                if (response.redirectTo)
                    headers["Location"] = response.redirectTo;
                return {
                    statusCode: response.statusCode,
                    headers,
                    body: head ? "" : response.markdown,
                    redirectTo: response.redirectTo,
                };
            }
        }
        if (!options.html)
            return null;
        const response = await options.html.renderRequest(request.url, {
            redirectLocaleCode: options.resolveRedirectLocaleCode?.(request),
            redirectStatusCode: 307,
        });
        const headers = { ...response.headers };
        if (options.markdown)
            withVary(headers, "Accept");
        if (response.redirectTo)
            headers["Location"] = response.redirectTo;
        return {
            statusCode: response.statusCode,
            headers,
            body: head ? "" : response.html,
            redirectTo: response.redirectTo,
        };
    };
}
//# sourceMappingURL=site.js.map